import { useTranslation } from 'react-i18next'
import { CheckCircle, Loader2 } from 'lucide-react'
import { useEnrollmentBatchStatus } from '@/hooks/useApi'

interface EnrollNotesProgressProps {
  batchId: string | null | undefined
}

/**
 * The HubSpot notes of the contacts just enrolled, written in background
 * (#386). Nothing is shown without a batch or if its progress cannot be read:
 * the enrollment is done either way.
 */
export function EnrollNotesProgress({ batchId }: EnrollNotesProgressProps) {
  const { t } = useTranslation('sequences')
  const { data, isError } = useEnrollmentBatchStatus(batchId)

  if (!batchId || isError || !data) return null

  const completed = data.total - data.pending
  const percent = data.total > 0 ? Math.round((completed / data.total) * 100) : 100

  if (data.pending > 0) {
    return (
      <div data-testid="enroll-notes-progress" className="space-y-2 pt-2 text-left">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          {t('enrollDialog.result.notes.writing', { completed, total: data.total })}
        </div>
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={data.total}
          aria-valuenow={completed}
        >
          <div className="h-full bg-primary transition-all" style={{ width: `${percent}%` }} />
        </div>
        <p className="text-xs text-muted-foreground">{t('enrollDialog.result.notes.canClose')}</p>
      </div>
    )
  }

  return (
    <div data-testid="enroll-notes-progress" className="space-y-1 pt-2 text-sm text-muted-foreground">
      <p className="flex items-center justify-center gap-2">
        <CheckCircle className="h-4 w-4 shrink-0 text-green-500" />
        {t('enrollDialog.result.notes.completed')}
      </p>
      {data.failed > 0 && (
        <p className="text-amber-700 dark:text-amber-400">
          {t('enrollDialog.result.notes.failed', { count: data.failed })}
        </p>
      )}
    </div>
  )
}
